import { useState, useEffect, useCallback } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import {
  APPRAISAL_PROGRAM,
  DATA_ELEMENTS,
  APPRAISAL_STATUSES,
} from '../api/appraisalConstants';

const mapDataValuesToAppraisal = (dataValues = []) => {
  const map = {};
  dataValues.forEach((dv) => {
    switch (dv.dataElement) {
      case DATA_ELEMENTS.APPRAISAL_STATUS:
        map.appraisalStatus = dv.value;
        break;
      case DATA_ELEMENTS.EMPLOYEE_SELF_RATING_TASK:
        map.employeeSelfRatingTask = dv.value;
        break;
      case DATA_ELEMENTS.EMPLOYEE_SELF_RATING_BEHAVIOURAL:
        map.employeeSelfRatingBehavioural = dv.value;
        break;
      case DATA_ELEMENTS.EMPLOYEE_COMMENTS:
        map.employeeSelfComments = dv.value;
        break;
      case DATA_ELEMENTS.MANAGER_RATING_TASK:
        map.managerRatingTask = dv.value;
        break;
      case DATA_ELEMENTS.MANAGER_RATING_BEHAVIOURAL:
        map.managerRatingBehavioural = dv.value;
        break;
      case DATA_ELEMENTS.MANAGER_COMMENTS:
        map.managerComments = dv.value;
        break;
      // NEW: Employee response fields
      case DATA_ELEMENTS.EMPLOYEE_RESPONSE_STATUS:
        map.employeeResponseStatus = dv.value;
        break;
      case DATA_ELEMENTS.EMPLOYEE_RESPONSE_REMARKS:
        map.employeeResponseRemarks = dv.value;
        break;
      case DATA_ELEMENTS.EMPLOYEE_ACCEPTED_RATING_TASK:
        map.employeeAcceptedRatingTask = dv.value;
        break;
      case DATA_ELEMENTS.EMPLOYEE_ACCEPTED_RATING_BEHAVIOURAL:
        map.employeeAcceptedRatingBehavioural = dv.value;
        break;
      default:
        break;
    }
  });

  // Set default status if not found
  map.appraisalStatus = map.appraisalStatus || APPRAISAL_STATUSES.DRAFT;
  
  // Initialize response fields if not present
  map.employeeResponseStatus = map.employeeResponseStatus || '';
  map.employeeResponseRemarks = map.employeeResponseRemarks || '';
  map.employeeAcceptedRatingTask = map.employeeAcceptedRatingTask || '';
  map.employeeAcceptedRatingBehavioural = map.employeeAcceptedRatingBehavioural || '';
  
  return map;
};

// Accept programStage as parameter
export const useAppraisalEvents = (positionTEI, programStage, orgUnit, engine) => {
  const [appraisalEvent, setAppraisalEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refetch = useCallback(async (isCancelled = () => false) => {
    if (!positionTEI || !programStage || !orgUnit || !engine) {
      setAppraisalEvent(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await engine.query({
        appraisal: {
          resource: 'events',
          params: {
            program: APPRAISAL_PROGRAM,
            programStage: programStage,
            trackedEntityInstance: positionTEI,
            orgUnit: orgUnit,
            ouMode: 'ACCESSIBLE',
            fields: 'event,dataValues[dataElement,value]',
            pageSize: 1,
          }
        },
      });

      if (isCancelled()) return;

      const event = response.appraisal?.events?.[0] || null;
      
      if (event) {
        const mappedData = mapDataValuesToAppraisal(event.dataValues);
        console.log('📋 [useAppraisalEvents] Loaded event:', {
          eventId: event.event,
          status: mappedData.appraisalStatus,
          employeeTask: mappedData.employeeSelfRatingTask,
          employeeBehavioural: mappedData.employeeSelfRatingBehavioural,
          managerTask: mappedData.managerRatingTask,
          managerBehavioural: mappedData.managerRatingBehavioural,
          responseStatus: mappedData.employeeResponseStatus,
        });
        
        setAppraisalEvent({
          eventId: event.event,
          ...mappedData,
        });
      } else {
        console.log('📋 [useAppraisalEvents] No existing appraisal found, creating blank');
        // Initialize blank appraisal
        setAppraisalEvent({
          eventId: null,
          appraisalStatus: APPRAISAL_STATUSES.DRAFT,
          employeeSelfRatingTask: '',
          employeeSelfRatingBehavioural: '',
          employeeSelfComments: '',
          managerRatingTask: '',
          managerRatingBehavioural: '',
          managerComments: '',
          employeeResponseStatus: '',
          employeeResponseRemarks: '',
          employeeAcceptedRatingTask: '',
          employeeAcceptedRatingBehavioural: '',
        });
      }
    } catch (error) {
      console.error('❌ [useAppraisalEvents] Error loading appraisal event:', error);
      if (!isCancelled()) {
        setError(error);
        setAppraisalEvent(null);
      }
    } finally {
      if (!isCancelled()) {
        setLoading(false);
      }
    }
  }, [positionTEI, programStage, orgUnit, engine]);

  useEffect(() => {
    let cancelled = false;

    refetch(() => cancelled);

    return () => {
      cancelled = true;
    };
  }, [refetch]);

  return { appraisalEvent, loading, error, refetch };
};